// Stamps the GA4 identity of the visitor on the cart, so it reaches the order and from there
// the purchase the server sends to GA4 (app/, webhook orders/create). The OpenAI Ads ids ride
// along for the same reason (see OPENAI_COOKIES).
//
// GA4 counts that purchase either way; what the ids buy is attribution. Without them a purchase
// that only the webhook brings —Addi, Sistecrédito, Wompi, where the customer pays off-site and
// often never comes back— lands in "(not set)" instead of the campaign that earned it. With the
// same client_id and session_id, GA4 joins it to the session of the click.
//
// Underscore attributes stay hidden in the checkout and the order emails, like _pdp_variant.
// Two moments cover every path to the checkout: the checkout button of the cart and the drawer,
// and the cart permalinks (/cart/<variant>:<quantity>) the "Buy now" buttons use, which skip
// the cart and only accept attributes in the link itself.

const CLIENT_COOKIE = '_ga';
// Cookie of the stream of the GA4 property; the GTM stream (_ga_TIEN112CA6) is another property.
const SESSION_COOKIE = '_ga_N2SPNZT2WD';
const CLIENT_ATTRIBUTE = '_ga_cid';
const SESSION_ATTRIBUTE = '_ga_sid';

// 2026-10-23: the same for the OpenAI Ads conversion the server sends (order_created): the ad
// click id and the browser reference its pixel keeps (snippets/pdp-openai-pixel.liquid). The
// pixel only writes them with measurement consent and removes them when it is revoked.
const OPENAI_COOKIES = { __oppref: '_oai_oppref', __obref: '_oai_obref' };

// 2026-10-26: Realize (Taboola) appends tblci=<click id> to every ad URL and the server needs it
// to report the purchase (app/src/lib/realize-conversion.ts). The landing URL only survives in the
// order when the purchase happens in the same visit, so the id is kept for the 30 days of the
// click-through window, and only with marketing consent, like the OpenAI cookies.
const REALIZE_PARAM = 'tblci';
const REALIZE_COOKIE = '_tbl_clid';
const REALIZE_ATTRIBUTE = '_tbl_clid';

const cookie = (name) => document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))?.[1] ?? '';

const realizeClick = new URLSearchParams(window.location.search).get(REALIZE_PARAM);
if (realizeClick) {
  const keep = () => {
    if (!window.Shopify?.customerPrivacy?.marketingAllowed()) return;
    document.cookie = `${REALIZE_COOKIE}=${encodeURIComponent(realizeClick)}; max-age=${30 * 86400}; path=/; SameSite=Lax; Secure`;
  };
  if (window.Shopify?.customerPrivacy) keep();
  else window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], (error) => !error && keep());
  document.addEventListener('visitorConsentCollected', keep);
}

// _ga = GA1.1.<client id>; _ga_<stream> = GS1.1.<session id>.<n>… or GS2.1.s<session id>$o…
const ids = () => {
  const attributes = {};
  const client = cookie(CLIENT_COOKIE).match(/^GA1\.\d+\.(\d+\.\d+)$/)?.[1];
  const session = cookie(SESSION_COOKIE).match(/^GS\d\.\d+\.s?(\d+)/)?.[1];
  if (client) {
    attributes[CLIENT_ATTRIBUTE] = client;
    if (session) attributes[SESSION_ATTRIBUTE] = session;
  }
  for (const [name, attribute] of Object.entries(OPENAI_COOKIES)) {
    const value = decodeURIComponent(cookie(name));
    if (value) attributes[attribute] = value;
  }
  const realize = decodeURIComponent(cookie(REALIZE_COOKIE));
  if (realize) attributes[REALIZE_ATTRIBUTE] = realize;
  return Object.keys(attributes).length ? attributes : null;
};

const root = window.Shopify?.routes?.root || '/';

// keepalive: the request survives the navigation to the checkout that the click starts.
const stamp = () => {
  const attributes = ids();
  if (!attributes) return;
  fetch(`${root}cart/update.js`, {
    method: 'POST',
    keepalive: true,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes }),
  }).catch(() => {});
};

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const link = target.closest('a[href]');
  if (link instanceof HTMLAnchorElement) {
    const url = new URL(link.href, window.location.href);
    if (/\/cart\/\d+:\d+/.test(url.pathname)) {
      for (const [name, value] of Object.entries(ids() ?? {})) url.searchParams.set(`attributes[${name}]`, value);
      link.href = url.toString();
      return;
    }
    if (url.pathname.startsWith(`${root}checkout`)) stamp();
    return;
  }
  if (target.closest('[name="checkout"], form[action*="/checkout"] [type="submit"]')) stamp();
}, true);
